import type { CSSProperties, ReactNode } from "react";
import { focusRing } from "../../utils";

export interface ListItemProps {
  id: string;
  title: ReactNode;
  subtitle?: ReactNode;
  meta?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  isSelected?: boolean;
  isHovered?: boolean;
  disabled?: boolean;
  /** If provided, renders as <a> with proper a11y; otherwise <button> */
  href?: string;
  /** Size variant affecting title/subtitle text sizes and spacing */
  size?: "sm" | "md" | "lg" | "xl";
  onClick?: (id: string) => void;
  onMouseEnter?: (id: string) => void;
  onMouseLeave?: (id: string) => void;
  className?: string;
  style?: CSSProperties;

  /**
   * PII type for masking the title (e.g., "full-name", "address")
   * When provided along with piiEntity, adds data-pii-* attributes
   */
  piiTitleType?: string;

  /**
   * PII type for masking the subtitle (e.g., "city-state", "email")
   */
  piiSubtitleType?: string;

  /**
   * Entity identifier for PII masking (e.g., "contact:ctc_abc123")
   */
  piiEntity?: string;
}

/**
 * ListItem
 *
 * A visual row component with consistent anatomy and states for use within List.
 * Supports both button and link variants with proper accessibility.
 *
 * Example usage:
 * ```tsx
 * <ListItem
 *   id="item-1"
 *   title="Site Name"
 *   subtitle="City, Region"
 *   meta={<span>12 devices</span>}
 *   leading={<Icon name="MapPin" />}
 *   trailing={<Button variant="icon" icon="Plus" />}
 *   isSelected={true}
 *   onClick={(id) => handleSelect(id)}
 * />
 * ```
 */
export function ListItem({
  id,
  title,
  subtitle,
  meta,
  leading,
  trailing,
  isSelected = false,
  isHovered = false,
  disabled = false,
  href,
  size = "md",
  onClick,
  onMouseEnter,
  onMouseLeave,
  className = "",
  style,
  piiTitleType,
  piiSubtitleType,
  piiEntity,
}: ListItemProps) {
  const handleClick = () => {
    if (!disabled && onClick) {
      onClick(id);
    }
  };

  const handleMouseEnter = () => {
    if (!disabled && onMouseEnter) {
      onMouseEnter(id);
    }
  };

  const handleMouseLeave = () => {
    if (!disabled && onMouseLeave) {
      onMouseLeave(id);
    }
  };

  // Size-based styles
  const sizeStyles = {
    sm: {
      container: "px-3 py-2 gap-2",
      title: "text-sm leading-tight",
      subtitle: "text-xs leading-tight",
      meta: "text-xs leading-tight",
      subtitleSpacing: "mt-1",
      metaSpacing: "mt-1",
    },
    md: {
      container: "px-4 py-3 gap-3",
      title: "text-base leading-tight",
      subtitle: "text-sm leading-tight",
      meta: "text-sm leading-tight",
      subtitleSpacing: "mt-1",
      metaSpacing: "mt-1",
    },
    lg: {
      container: "px-4 py-4 gap-3",
      title: "text-lg leading-tight",
      subtitle: "text-base leading-tight",
      meta: "text-sm leading-tight",
      subtitleSpacing: "mt-1",
      metaSpacing: "mt-1",
    },
    xl: {
      container: "px-5 py-5 gap-4",
      title: "text-xl leading-tight",
      subtitle: "text-base leading-tight",
      meta: "text-base leading-tight",
      subtitleSpacing: "mt-1.5",
      metaSpacing: "mt-1.5",
    },
  };

  const currentSize = sizeStyles[size];

  // Base styles for the row container
  const baseStyles = [
    "flex items-center w-full text-left transition-all duration-200 ease-in-out",
    currentSize.container,
    "border-l-2 border-l-transparent", // Subtle left accent bar for selection
    "rounded-sm", // Rounded corners for better visual appeal
    focusRing(),
  ];

  // State-based styles
  const stateStyles = [
    // Hover state
    isHovered && !isSelected && "bg-background-hover",
    // Selected state - more subtle and modern
    isSelected && "bg-action-default/5 border-l-action-default shadow-sm",
    // Disabled state
    disabled && "opacity-50 cursor-not-allowed",
    // Interactive state
    !disabled && !isSelected && "hover:bg-background-hover",
  ];

  const composedClassName = [...baseStyles, ...stateStyles, className].filter(Boolean).join(" ");

  // Content structure
  const content = (
    <>
      {/* Leading visual - flexible container that adapts to content */}
      {leading && <div className="flex-shrink-0 flex items-center justify-center">{leading}</div>}

      {/* Main content area */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          {/* Title, subtitle, and meta stack */}
          <div className="flex-1 min-w-0">
            <div
              className={`text-text-primary font-medium truncate ${currentSize.title}`}
              title={typeof title === "string" ? title : undefined}
              data-pii-type={piiTitleType && piiEntity ? piiTitleType : undefined}
              data-pii-entity={piiTitleType && piiEntity ? piiEntity : undefined}
            >
              {title}
            </div>
            {subtitle && (
              <div
                className={`text-text-secondary truncate ${currentSize.subtitle} ${currentSize.subtitleSpacing}`}
                title={typeof subtitle === "string" ? subtitle : undefined}
                data-pii-type={piiSubtitleType && piiEntity ? piiSubtitleType : undefined}
                data-pii-entity={piiSubtitleType && piiEntity ? piiEntity : undefined}
              >
                {subtitle}
              </div>
            )}
            {/* Meta area - below title/subtitle with room to grow */}
            {meta && <div className={`text-text-secondary ${currentSize.meta} ${currentSize.metaSpacing}`}>{meta}</div>}
          </div>
        </div>
      </div>

      {/* Trailing actions */}
      {trailing && <div className="flex-shrink-0">{trailing}</div>}
    </>
  );

  // Render as link or button based on href
  if (href) {
    return (
      <a
        href={href}
        className={composedClassName}
        style={style}
        // Don't call onClick for anchors - let browser handle navigation
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-selected={isSelected}
        aria-current={isSelected ? "page" : undefined}
        tabIndex={disabled ? -1 : 0}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={composedClassName}
      style={style}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      disabled={disabled}
      aria-selected={isSelected}
      tabIndex={disabled ? -1 : 0}
    >
      {content}
    </button>
  );
}
